"use client";
import Head from "next/head";
import Image from "next/image";
import { useEffect, useState } from "react";

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const hiddenScroll = document.getElementsByClassName("scrollbar-hide")[0];

    const handleScroll = () => {
      if (hiddenScroll.scrollTop > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    hiddenScroll.addEventListener("scroll", handleScroll);
    return () => hiddenScroll.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    const hiddenScroll = document.getElementsByClassName("scrollbar-hide")[0];
    hiddenScroll.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const links = document.querySelectorAll("a[href^='#']");
    links.forEach((link: unknown) => {
      (link as Element).addEventListener("click", (e) => {
        e.preventDefault();
        const section = document.querySelector((link as { hash: string }).hash);
        if (section) {
          section.scrollIntoView({
            behavior: "smooth",
          });
        }
      });
    });
  }, []);

  return (
    <>
      <Head>
        <title>Spielcrypto | Full-Stack & Web3 Development</title>
        <meta
          name="description"
          content="Cutting-edge full-stack & Web3 development using React, Next.js, NestJS, and more."
        />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Fixed Navigation Menu */}
        <header className="fixed top-0 left-0 w-full z-50">
          <nav className="flex justify-between items-center py-4 px-8 bg-gradient-to-r from-purple-800 to-indigo-700 shadow-md">
            <div className="flex items-center">
              <Image
                width={100}
                height={100}
                src="/logo.png"
                alt="Spielcrypto Logo"
                className="h-12 w-12 mr-3 rounded-full"
              />
              <span className="text-2xl font-bold text-white">Spielcrypto</span>
            </div>
            {/* Hamburger button for mobile */}
            <div className="sm:hidden">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="text-white focus:outline-none"
                aria-label="Toggle Menu"
              >
                {menuOpen ? (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
            {/* Desktop Menu */}
            <ul className="hidden sm:flex space-x-6 font-medium">
              <li>
                <a className="text-white hover:text-gray-200" href="#hero">
                  Home
                </a>
              </li>
              <li>
                <a className="text-white hover:text-gray-200" href="#about">
                  About
                </a>
              </li>
              <li>
                <a className="text-white hover:text-gray-200" href="#services">
                  Services
                </a>
              </li>
              <li>
                <a className="text-white hover:text-gray-200" href="#portfolio">
                  Portfolio
                </a>
              </li>
              <li>
                <a className="text-white hover:text-gray-200" href="#partners">
                  Partners
                </a>
              </li>
              <li>
                <a className="text-white hover:text-gray-200" href="#contact">
                  Contact
                </a>
              </li>
            </ul>
          </nav>
          {/* Mobile Dropdown Menu */}
          {menuOpen && (
            <ul className="sm:hidden bg-gradient-to-r from-purple-800 to-indigo-700 px-8 py-4">
              <li className="mb-2">
                <a
                  onClick={() => setMenuOpen(false)}
                  className="block text-white hover:text-gray-200"
                  href="#hero"
                >
                  Home
                </a>
              </li>
              <li className="mb-2">
                <a
                  onClick={() => setMenuOpen(false)}
                  className="block text-white hover:text-gray-200"
                  href="#about"
                >
                  About
                </a>
              </li>
              <li className="mb-2">
                <a
                  onClick={() => setMenuOpen(false)}
                  className="block text-white hover:text-gray-200"
                  href="#services"
                >
                  Services
                </a>
              </li>
              <li className="mb-2">
                <a
                  onClick={() => setMenuOpen(false)}
                  className="block text-white hover:text-gray-200"
                  href="#portfolio"
                >
                  Portfolio
                </a>
              </li>
              <li className="mb-2">
                <a
                  onClick={() => setMenuOpen(false)}
                  className="block text-white hover:text-gray-200"
                  href="#partners"
                >
                  Partners
                </a>
              </li>
              <li className="mb-2">
                <a
                  onClick={() => setMenuOpen(false)}
                  className="block text-white hover:text-gray-200"
                  href="#contact"
                >
                  Contact
                </a>
              </li>
            </ul>
          )}
        </header>

        <main>
          {/* Hero Section with Parallax Effect */}
          <section
            id="hero"
            className="h-screen bg-fixed bg-center bg-cover bg-purple-800 sm:bg-[url('/background2.webp')]"
          >
            <div className="flex sm:hidden items-center justify-center h-full bg-black/30">
              <div className="text-center text-white px-4">
                <h1 className="text-5xl font-bold mb-4">
                  Welcome to Spielcrypto
                </h1>
                <p className="text-xl">
                  Full-Stack &amp; Web3 Development for the Future
                </p>
              </div>
            </div>
          </section>

          {/* About Section */}
          <section id="about" className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <h2 className="text-3xl font-bold text-center text-purple-700 mb-8">
                About Us
              </h2>
              <p className="text-gray-700 text-center max-w-3xl mx-auto">
                Spielcrypto is at the forefront of technological innovation,
                building robust full-stack web applications using modern
                frameworks like React, Next.js, and NestJS. Our experienced team
                excels in deploying microservices, managing SQL &amp; NoSQL
                databases, and developing dynamic solutions in Web3, AI,
                Fintech, SaaS, and social apps.
              </p>
            </div>
          </section>

          {/* Services Section */}
          <section id="services" className="py-20 bg-gray-100">
            <div className="container mx-auto px-6">
              <h2 className="text-3xl font-bold text-center text-purple-700 mb-8">
                Our Services
              </h2>
              <div className="flex flex-wrap justify-center gap-6">
                <div className="bg-white shadow-lg rounded-lg p-6 w-full sm:w-1/3 md:w-1/4">
                  <h3 className="text-xl font-semibold text-blue-700 mb-2">
                    Web App Development
                  </h3>
                  <p className="text-gray-600">
                    Create modern, scalable web apps that bring your vision to
                    life.
                  </p>
                </div>
                <div className="bg-white shadow-lg rounded-lg p-6 w-full sm:w-1/3 md:w-1/4">
                  <h3 className="text-xl font-semibold text-blue-700 mb-2">
                    Web3 Solutions
                  </h3>
                  <p className="text-gray-600">
                    Innovative blockchain and crypto integrations for the
                    digital era.
                  </p>
                </div>
                <div className="bg-white shadow-lg rounded-lg p-6 w-full sm:w-1/3 md:w-1/4">
                  <h3 className="text-xl font-semibold text-blue-700 mb-2">
                    AI Applications
                  </h3>
                  <p className="text-gray-600">
                    Intelligent AI solutions that drive innovation and
                    efficiency.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Portfolio Section with Parallax Effect and Projects */}
          <section
            id="portfolio"
            className="bg-fixed bg-center bg-cover bg-indigo-800 sm:bg-[url('/background.webp')]"
          >
            <div className="bg-black/30 py-20">
              <div className="container mx-auto px-6">
                <h2 className="text-3xl font-bold text-center text-white mb-12">
                  Portfolio
                </h2>
                <div className="flex flex-wrap justify-center gap-8">
                  {/* Inferenco Project Card */}
                  <div className="bg-white rounded-lg shadow-lg p-6 w-full sm:w-1/2 lg:w-1/3">
                    <div className="flex items-center space-x-4 mb-4">
                      <Image
                        width={120}
                        height={120}
                        src="/flame.png"
                        alt="Inferenco Logo"
                        className="w-12 h-12 object-contain"
                      />
                      <h3 className="text-xl font-bold text-purple-700">
                        Inferenco
                      </h3>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Developing blockchain applications, AI tools, and wallets for Cedra Network.
                      Building transparent, Web3-integrated solutions for crypto communities and DeFi partners.
                    </p>
                    <div className="mt-4">
                      <a
                        href="https://inferenco.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 font-medium"
                      >
                        Visit site
                      </a>
                    </div>
                  </div>
                  {/* Handle Finder Project Card */}
                  <div className="bg-white rounded-lg shadow-lg p-6 w-full sm:w-1/2 lg:w-1/3">
                    <div className="flex items-center space-x-4 mb-4">
                      <Image
                        width={120}
                        height={120}
                        src="/handlefinder-logo.jpg"
                        alt="Handle Finder Logo"
                        className="w-12 h-12 object-contain"
                      />
                      <h3 className="text-xl font-bold text-purple-700">
                        Handle Finder
                      </h3>
                    </div>
                    <p className="text-gray-700 text-sm">
                      A SocialFi analytics platform providing trending users,
                      tokens, and crypto news, complete with a friendly UI and
                      developer API.
                      <a
                        href="https://handlefinder.xyz/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 font-medium ml-1"
                      >
                        Visit site
                      </a>
                      .
                    </p>
                  </div>
                  {/* Libree Project Card */}
                  <div className="bg-white rounded-lg shadow-lg p-6 w-full sm:w-1/2 lg:w-1/3">
                    <div className="flex items-center space-x-4 mb-4">
                      <Image
                        width={120}
                        height={120}
                        src="/libree-logo.png"
                        alt="Libree Logo"
                        className="w-12 h-12 object-contain"
                      />
                      <h3 className="text-xl font-bold text-purple-700">
                        Libree
                      </h3>
                    </div>
                    <p className="text-gray-700 text-sm">
                      A GameFi app where users play HTML and Unity games to win
                      prizes through events and competitions organized by
                      sponsors.
                      <a
                        href="https://libree.xyz/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 font-medium ml-1"
                      >
                        Learn more
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Partners Section */}
          <section id="partners" className="py-20 bg-gray-100">
            <div className="container mx-auto px-6">
              <h2 className="text-3xl font-bold text-center text-purple-700 mb-8">
                Our Partners
              </h2>
              <div className="flex flex-wrap justify-center items-center gap-8">
                {/* Astana Hub */}
                <div className="flex flex-col items-center">
                  <Image
                    width={200}
                    height={200}
                    src="/astana_hub_logo.png"
                    alt="Astana Hub Logo"
                    className="h-24 object-contain mb-2"
                  />
                  <span className="text-gray-800 font-semibold">
                    Astana Hub
                  </span>
                </div>
                {/* Singularity Shift */}
                <div className="flex flex-col items-center">
                  <Image
                    width={200}
                    height={200}
                    src="/sshift-logo.png"
                    alt="Singularity Shift Logo"
                    className="h-24 object-contain mb-2"
                  />
                  <span className="text-gray-800 font-semibold">
                    Singularity Shift
                  </span>
                </div>
                {/* Libree */}
                <div className="flex flex-col items-center">
                  <Image
                    width={200}
                    height={200}
                    src="/libree-logo.png"
                    alt="Libree Logo"
                    className="h-24 object-contain mb-2"
                  />
                  <span className="text-gray-800 font-semibold">Libree</span>
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <h2 className="text-3xl font-bold text-center text-purple-700 mb-8">
                Contact Us
              </h2>
              <p className="text-center text-gray-700 max-w-xl mx-auto mb-8">
                Ready to elevate your digital presence? Connect with us today.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8">
                <a
                  href="mailto:fjnavarro@spielcrypto.com"
                  className="flex items-center space-x-2 bg-purple-700 text-white font-bold py-3 px-6 rounded-full shadow-lg hover:bg-purple-600 transition"
                >
                  <Image
                    width={80}
                    height={60}
                    src="/email.jpg"
                    alt="Email Logo"
                    className="w-8 h-6"
                  />
                  <span>fjnavarro@spielcrypto.com</span>
                </a>
                <a
                  href="https://t.me/spielrs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-blue-500 text-white font-bold py-3 px-6 rounded-full shadow-lg hover:bg-blue-400 transition"
                >
                  <Image
                    width={60}
                    height={60}
                    src="/telegram.webp"
                    alt="Telegram Logo"
                    className="w-6 h-6"
                  />
                  <span>Telegram</span>
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-gray-800 text-white text-center py-4">
          <p>&copy; 2025 Spielcrypto. All Rights Reserved.</p>
        </footer>

        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="sm:hidden fixed bottom-4 right-4 bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition-colors"
          >
            ↑
          </button>
        )}
      </div>
    </>
  );
}
